// vim: set ts=2 sts=2 sw=2 et:

import { REHYDRATE } from "redux-persist";
import type { OverwriteStoreAction } from "../../types/actionTypes";
import { defaultWrpfLiveState, normalizeWrpfLiveState, WrpfLiveState } from "./platformLiveProfile";
import type { WrpfLiveAction } from "./platformLiveProfileActions";

type Action = WrpfLiveAction | OverwriteStoreAction | { type: string };

export default function platformLiveProfileReducer(
  state: WrpfLiveState = defaultWrpfLiveState(),
  action: Action,
): WrpfLiveState {
  switch (action.type) {
    case "ACTIVATE_PLATFORM_LIVE_PROFILE": {
      const activate = action as Extract<WrpfLiveAction, { type: "ACTIVATE_PLATFORM_LIVE_PROFILE" }>;
      return normalizeWrpfLiveState({
        ...state,
        activeProfile: activate.profile,
        helperProfileCandidate: activate.profile.dmxConnection === "helper" ? activate.profile : null,
      });
    }

    case "SET_RECORD_MARKING_MODE": {
      const setMode = action as Extract<WrpfLiveAction, { type: "SET_RECORD_MARKING_MODE" }>;
      return {
        ...state,
        recordMarking: {
          ...state.recordMarking,
          mode: setMode.mode,
        },
      };
    }

    case "SET_RECORD_TEST_TOOLS_ENABLED": {
      const setEnabled = action as Extract<WrpfLiveAction, { type: "SET_RECORD_TEST_TOOLS_ENABLED" }>;
      return {
        ...state,
        developerSettings: {
          ...state.developerSettings,
          recordTestToolsEnabled: setEnabled.enabled,
        },
      };
    }

    case "SET_REGISTRATION_PAGINATION_ENABLED": {
      const setEnabled = action as Extract<WrpfLiveAction, { type: "SET_REGISTRATION_PAGINATION_ENABLED" }>;
      return {
        ...state,
        developerSettings: {
          ...state.developerSettings,
          registrationPaginationEnabled: setEnabled.enabled,
        },
      };
    }

    case "SET_PLATFORM_LIVE_BRIDGE_PROVIDER": {
      const setProvider = action as Extract<WrpfLiveAction, { type: "SET_PLATFORM_LIVE_BRIDGE_PROVIDER" }>;
      return {
        ...state,
        developerSettings: {
          ...state.developerSettings,
          bridgeProviderId: setProvider.providerId,
        },
      };
    }

    case "SET_RECORD_ATTEMPT_OVERRIDE": {
      const setOverride = action as Extract<WrpfLiveAction, { type: "SET_RECORD_ATTEMPT_OVERRIDE" }>;
      return {
        ...state,
        recordMarking: {
          ...state.recordMarking,
          overrides: {
            ...state.recordMarking.overrides,
            [setOverride.attemptKey]: setOverride.category,
          },
        },
      };
    }

    case "CLEAR_RECORD_ATTEMPT_OVERRIDE": {
      const clearOverride = action as Extract<WrpfLiveAction, { type: "CLEAR_RECORD_ATTEMPT_OVERRIDE" }>;
      const overrides = { ...state.recordMarking.overrides };
      delete overrides[clearOverride.attemptKey];
      return {
        ...state,
        recordMarking: {
          ...state.recordMarking,
          overrides,
        },
      };
    }

    case "CAPTURE_RESOLVED_RECORD_ATTEMPT": {
      const capture = action as Extract<WrpfLiveAction, { type: "CAPTURE_RESOLVED_RECORD_ATTEMPT" }>;
      const categoriesWhenResolved = { ...state.recordMarking.categoriesWhenResolved };
      if (capture.category === undefined) {
        delete categoriesWhenResolved[capture.attemptKey];
      } else {
        categoriesWhenResolved[capture.attemptKey] = capture.category;
      }
      return {
        ...state,
        recordMarking: {
          ...state.recordMarking,
          categoriesWhenResolved,
        },
      };
    }

    case "DEACTIVATE_PLATFORM_LIVE_PROFILE":
      return {
        ...defaultWrpfLiveState(),
        developerSettings: state.developerSettings,
      };

    case "CLEAR_HELPER_PROFILE_CANDIDATE":
      return { ...state, helperProfileCandidate: null };

    case REHYDRATE:
      // Never restore a live profile directly. A Helper candidate is retained only because the launcher verifies
      // its exact room before promoting it; record policy remains browser-local for cross-window use.
      return normalizeWrpfLiveState({
        activeProfile: null,
        helperProfileCandidate:
          (action as { payload?: { wrpfLive?: Partial<WrpfLiveState> } | null }).payload?.wrpfLive
            ?.helperProfileCandidate ?? null,
        recordMarking:
          (action as { payload?: { wrpfLive?: Partial<WrpfLiveState> } | null }).payload?.wrpfLive?.recordMarking ??
          defaultWrpfLiveState().recordMarking,
        developerSettings:
          (action as { payload?: { wrpfLive?: Partial<WrpfLiveState> } | null }).payload?.wrpfLive?.developerSettings ??
          defaultWrpfLiveState().developerSettings,
      });

    case "OVERWRITE_STORE": {
      const overwrite = action as OverwriteStoreAction;
      const incoming = overwrite.store.wrpfLive;
      // The loaded file/bundle carries its own active live profile → adopt it.
      if (incoming?.activeProfile != null) {
        return normalizeWrpfLiveState(incoming);
      }
      // It doesn't (files/bundles don't serialize the live profile). KEEP the running live profile so
      // loading a meet file/bundle does NOT turn off the live server — but only for the same (or an
      // unknown/tournament-less, e.g. vanilla) tournament. A DIFFERENT tournament resets it. Mirrors
      // the wrpfSession keep-session guard.
      const active = state.activeProfile;
      const incomingTournamentId = overwrite.store.wrpfSession?.tournamentId;
      const sameOrUnknownTournament =
        incomingTournamentId == null || active?.tournamentId == null || active.tournamentId === incomingTournamentId;
      if (active !== null && sameOrUnknownTournament) {
        return state;
      }
      return defaultWrpfLiveState();
    }

    default:
      return normalizeWrpfLiveState(state);
  }
}
